import React, { useEffect, useRef, useMemo } from 'react';
import { 
  POSE_LANDMARKS, 
  POSE_CONNECTIONS,
  generateIdealPose,
  evaluateFormQuality,
  calculateJointAngles
} from '../hooks/usePoseDetection3D';

// Color scheme for form quality
const QUALITY_COLORS = {
  excellent: '#00FF00', // Bright green
  good: '#CCFF00',      // Volt green
  warning: '#FFB800',   // Orange
  danger: '#FF3333',    // Red
};

// Arrow directions for common corrections
const CORRECTION_ARROWS = {
  kneesOut: { dx: 0.15, dy: 0 },
  kneesIn: { dx: -0.15, dy: 0 },
  chestUp: { dx: 0, dy: -0.1 },
  hipsBack: { dx: -0.1, dy: 0 },
  deeper: { dx: 0, dy: 0.1 },
};

const AROverlay = ({ 
  landmarks2D,
  landmarks3D,
  angles,
  exerciseName,
  canvasWidth,
  canvasHeight,
  showGhostPose = true,
  showColorSkeleton = true,
  showGuides = true,
  showDepthLine = true,
}) => {
  const canvasRef = useRef(null);
  
  // Calculate ideal pose based on body proportions
  const idealPose = useMemo(() => {
    if (!landmarks3D || !exerciseName) return null;
    return generateIdealPose(landmarks3D, exerciseName, 'bottom');
  }, [landmarks3D, exerciseName]);
  
  // Evaluate form quality for color coding
  const formQuality = useMemo(() => {
    if (!angles || !exerciseName) return {};
    return evaluateFormQuality(angles, exerciseName);
  }, [angles, exerciseName]);
  
  // Determine corrections needed
  const corrections = useMemo(() => {
    if (!angles || !exerciseName) return [];
    const corrections = [];
    const lowerExercise = exerciseName.toLowerCase();
    
    if (lowerExercise.includes('squat')) {
      // Knee valgus correction
      if ((angles.leftKneeValgus || 0) > 5 || (angles.rightKneeValgus || 0) > 5) {
        corrections.push({
          type: 'kneesOut',
          joints: [POSE_LANDMARKS.LEFT_KNEE, POSE_LANDMARKS.RIGHT_KNEE],
          message: 'Push knees out'
        });
      }
      
      // Depth correction
      const avgKnee = ((angles.leftKnee || 180) + (angles.rightKnee || 180)) / 2;
      if (avgKnee > 120) {
        corrections.push({
          type: 'deeper',
          joints: [POSE_LANDMARKS.LEFT_HIP, POSE_LANDMARKS.RIGHT_HIP],
          message: 'Go deeper'
        });
      }
      
      // Chest up correction
      if ((angles.spineAngle || 90) < 50) {
        corrections.push({
          type: 'chestUp',
          joints: [POSE_LANDMARKS.LEFT_SHOULDER, POSE_LANDMARKS.RIGHT_SHOULDER],
          message: 'Chest up'
        });
      }
    }
    
    if (lowerExercise.includes('deadlift')) {
      // Back rounding
      if ((angles.spineAngle || 90) < 70) {
        corrections.push({
          type: 'chestUp',
          joints: [POSE_LANDMARKS.LEFT_SHOULDER, POSE_LANDMARKS.RIGHT_SHOULDER],
          message: 'Flatten back'
        });
      }
    }
    
    return corrections;
  }, [angles, exerciseName]);
  
  // Draw AR overlay
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !landmarks2D) return;
    
    try {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      
      // Helper to convert normalized coords to canvas coords
      const toCanvas = (point) => {
        if (!point) return { x: 0, y: 0 };
        return {
          x: point.x * canvasWidth,
          y: point.y * canvasHeight
        };
      };
    
    // 1. Draw ghost/ideal pose (semi-transparent)
    if (showGhostPose && idealPose) {
      ctx.globalAlpha = 0.3;
      ctx.strokeStyle = '#00FFFF';
      ctx.lineWidth = 8;
      ctx.setLineDash([10, 5]);
      
      // Draw ideal pose connections
      POSE_CONNECTIONS.forEach(([startIdx, endIdx]) => {
        const start = toCanvas(idealPose[startIdx]);
        const end = toCanvas(idealPose[endIdx]);
        
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
      });
      
      // Draw ideal pose joints
      ctx.fillStyle = '#00FFFF';
      idealPose.forEach((point, idx) => {
        if (idx < 11 || idx > 32) return; // Skip face landmarks
        const pos = toCanvas(point);
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 8, 0, Math.PI * 2);
        ctx.fill();
      });
      
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);
    }
    
    // 2. Draw color-coded skeleton
    if (showColorSkeleton) {
      ctx.lineWidth = 4;
      
      // Draw connections with gradient based on connected joint quality
      POSE_CONNECTIONS.forEach(([startIdx, endIdx]) => {
        const startQuality = formQuality[startIdx] || 'good';
        const endQuality = formQuality[endIdx] || 'good';
        const start = toCanvas(landmarks2D[startIdx]);
        const end = toCanvas(landmarks2D[endIdx]);
        
        // Use gradient between the two joint colors
        const gradient = ctx.createLinearGradient(start.x, start.y, end.x, end.y);
        gradient.addColorStop(0, QUALITY_COLORS[startQuality]);
        gradient.addColorStop(1, QUALITY_COLORS[endQuality]);
        
        ctx.strokeStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
      });
      
      // Draw joints with quality colors
      landmarks2D.forEach((point, idx) => {
        if (idx < 11 || idx > 32) return; // Skip face landmarks
        
        const pos = toCanvas(point);
        const quality = formQuality[idx] || 'good';
        const color = QUALITY_COLORS[quality];
        
        // Outer glow for warning/danger
        if (quality === 'warning' || quality === 'danger') {
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, 15, 0, Math.PI * 2);
          ctx.fillStyle = color + '40';
          ctx.fill();
        }
        
        // Joint circle
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 8, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    }
    
    // 3. Draw correction arrows
    if (showGuides && corrections.length > 0) {
      corrections.forEach(correction => {
        const arrow = CORRECTION_ARROWS[correction.type];
        if (!arrow) return;
        
        correction.joints.forEach(jointIdx => {
          const point = toCanvas(landmarks2D[jointIdx]);
          const endX = point.x + arrow.dx * canvasWidth;
          const endY = point.y + arrow.dy * canvasHeight;
          
          // Draw arrow
          ctx.strokeStyle = '#FF6600';
          ctx.fillStyle = '#FF6600';
          ctx.lineWidth = 4;
          
          // Arrow line
          ctx.beginPath();
          ctx.moveTo(point.x, point.y);
          ctx.lineTo(endX, endY);
          ctx.stroke();
          
          // Arrow head
          const angle = Math.atan2(endY - point.y, endX - point.x);
          const headSize = 15;
          ctx.beginPath();
          ctx.moveTo(endX, endY);
          ctx.lineTo(
            endX - headSize * Math.cos(angle - Math.PI / 6),
            endY - headSize * Math.sin(angle - Math.PI / 6)
          );
          ctx.lineTo(
            endX - headSize * Math.cos(angle + Math.PI / 6),
            endY - headSize * Math.sin(angle + Math.PI / 6)
          );
          ctx.closePath();
          ctx.fill();
        });
        
        // Draw correction text
        if (correction.joints.length > 0) {
          const avgPos = correction.joints.reduce((acc, idx) => {
            const pos = toCanvas(landmarks2D[idx]);
            return { x: acc.x + pos.x / correction.joints.length, y: acc.y + pos.y / correction.joints.length };
          }, { x: 0, y: 0 });
          
          ctx.font = 'bold 16px Barlow Condensed';
          ctx.fillStyle = '#FF6600';
          ctx.textAlign = 'center';
          ctx.fillText(correction.message, avgPos.x, avgPos.y - 30);
        }
      });
    }
    
    // 4. Draw depth target line (for squats/lunges)
    if (showDepthLine && exerciseName) {
      const lowerExercise = exerciseName.toLowerCase();
      
      if (lowerExercise.includes('squat') || lowerExercise.includes('lunge')) {
        // Draw horizontal line at target depth (thighs parallel)
        const leftHip = toCanvas(landmarks2D[POSE_LANDMARKS.LEFT_HIP]);
        const rightHip = toCanvas(landmarks2D[POSE_LANDMARKS.RIGHT_HIP]);
        const hipY = (leftHip.y + rightHip.y) / 2;
        
        // Target depth line (where knees should be for parallel)
        const avgKnee = ((angles?.leftKnee || 180) + (angles?.rightKnee || 180)) / 2;
        const isAtDepth = avgKnee < 100;
        
        ctx.strokeStyle = isAtDepth ? '#00FF00' : '#FFFF00';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        
        // Draw line across the frame at hip level
        ctx.beginPath();
        ctx.moveTo(50, hipY);
        ctx.lineTo(canvasWidth - 50, hipY);
        ctx.stroke();
        
        // Label
        ctx.setLineDash([]);
        ctx.font = 'bold 14px Barlow Condensed';
        ctx.fillStyle = isAtDepth ? '#00FF00' : '#FFFF00';
        ctx.textAlign = 'left';
        ctx.fillText(isAtDepth ? 'PARALLEL' : 'TARGET DEPTH', 60, hipY - 10);
      }
    }
    
    // 5. Draw range of motion arc (for current joint)
    if (showGuides && landmarks2D && angles) {
      const lowerExercise = (exerciseName || '').toLowerCase();
      
      if (lowerExercise.includes('squat')) {
        // Draw arc showing knee flexion range
        const leftKnee = toCanvas(landmarks2D[POSE_LANDMARKS.LEFT_KNEE]);
        const leftHip = toCanvas(landmarks2D[POSE_LANDMARKS.LEFT_HIP]);
        const leftAnkle = toCanvas(landmarks2D[POSE_LANDMARKS.LEFT_ANKLE]);
        
        // Calculate current angle and ideal range
        const currentAngle = angles.leftKnee || 180;
        const startAngle = Math.atan2(leftHip.y - leftKnee.y, leftHip.x - leftKnee.x);
        const endAngle = Math.atan2(leftAnkle.y - leftKnee.y, leftAnkle.x - leftKnee.x);
        
        // Draw arc
        ctx.strokeStyle = '#CCFF0060';
        ctx.lineWidth = 20;
        ctx.beginPath();
        ctx.arc(leftKnee.x, leftKnee.y, 40, startAngle, endAngle, true);
        ctx.stroke();
        
        // Current angle indicator
        ctx.fillStyle = '#CCFF00';
        ctx.font = 'bold 18px Barlow Condensed';
        ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(currentAngle)}°`, leftKnee.x + 60, leftKnee.y);
      }
    }
    } catch (err) {
      console.error('AR Overlay rendering error:', err);
    }
    
  }, [landmarks2D, idealPose, formQuality, corrections, exerciseName, canvasWidth, canvasHeight, showGhostPose, showColorSkeleton, showGuides, showDepthLine, angles]);
  
  return (
    <canvas
      ref={canvasRef}
      width={canvasWidth}
      height={canvasHeight}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ transform: 'scaleX(-1)' }}
    />
  );
};

export default AROverlay;
